import { Divider, Group, Stack, Stepper, Text } from '@mantine/core';
import { IconClock } from '@tabler/icons-react';
import moment from 'moment';
import { useMemo } from 'react';

import { TSDData } from '@e-origin/contracts';
import { EORIGIN_DATE_FORMAT } from '@e-origin/shared';

import { DAY_IN_MS } from '@constants';
import { WriteOffTable } from './WriteOffTable';

interface TSDViewAuditViewProps {
  tsd: TSDData;
}

export const TSDViewAuditView: React.FC<TSDViewAuditViewProps> = ({ tsd }) => {
  const timelineData = useMemo(() => {
    const grossMassDeclared = parseFloat(tsd.consignment?.master?.totalGrossMass || '0');
    const packagesDeclared = parseInt(tsd.consignment?.master?.totalNumberOfPackages || '0', 10);

    const writeOffs = tsd.customsState?.writeOff || [];
    const latestWriteOff = writeOffs.length > 0 ? writeOffs[writeOffs.length - 1] : null;

    const grossMassWrittenOff = parseFloat(latestWriteOff?.grossMassWrittenOff || '0');
    const packagesWrittenOff = parseInt(latestWriteOff?.numberOfPackagesWrittenOff || '0', 10);

    return {
      grossMassDeclared,
      packagesDeclared,
      grossMassWrittenOff,
      packagesWrittenOff,
    };
  }, [tsd]);

  const startDate = tsd.customsState?.startOfTemporaryStorage ? new Date(tsd.customsState.startOfTemporaryStorage) : null;

  const { grossMassDeclared, packagesDeclared, grossMassWrittenOff, packagesWrittenOff } = timelineData;

  const timelineEvents = useMemo(() => {
    return [
      {
        date: startDate,
        title: 'TSD Started',
        bullet: <IconClock size={12} />,
      },
      {
        date: startDate ? new Date(startDate.getTime() + 60 * DAY_IN_MS) : null,
        title: 'Day 60',
        bullet: <IconClock size={12} />,
      },
      {
        date: startDate ? new Date(startDate.getTime() + 70 * DAY_IN_MS) : null,
        title: 'Day 70',
        bullet: <IconClock size={12} />,
      },
      {
        date: startDate ? new Date(startDate.getTime() + 80 * DAY_IN_MS) : null,
        title: 'Day 80',
        bullet: <IconClock size={12} />,
      },
      {
        date: startDate ? new Date(startDate.getTime() + 90 * DAY_IN_MS) : null,
        title: 'Day 90 — Final write-off',
        bullet: <IconClock size={12} />,
      },
    ];
  }, [startDate]);

  if (!tsd?.messageInfo?.MRN || !tsd.customsState?.startOfTemporaryStorage) {
    return (
      <Stack p="xl" align="center">
        <Text fw={700}>Not started — awaiting activation</Text>
      </Stack>
    );
  }

  return (
    <Group p="xl" align="flex-start" gap="xl">
      <Stack>
        <Text fw={700}>Write-off Ratios</Text>

        <Stack gap="xl">
          <Text>
            Mass: <strong>{grossMassWrittenOff}</strong> / {grossMassDeclared}
          </Text>
          <Text>
            Packages: <strong>{packagesWrittenOff}</strong> / {packagesDeclared}
          </Text>
        </Stack>

        <Divider />

        <Stepper active={-1} orientation={'horizontal'} size="md">
          {timelineEvents.map((event, index) => (
            <Stepper.Step key={index} label={event.title} description={moment(event.date).format(EORIGIN_DATE_FORMAT)} icon={<IconClock size={12} />} />
          ))}
        </Stepper>

        <Divider />
      </Stack>

      <WriteOffTable tsd={tsd} />
    </Group>
  );
};
